import React, { useState, useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Gallery() {
    const { t } = useTranslation();
  const images = [
    "/images/p2.webp",
    "/images/p3.jpg",
    "/images/p4.jpg",
    "/images/p5.jpg",
    "/images/p8.webp",
    "/images/p6.webp",
    "/images/p7.jpg",
    "/images/p9.jpg",
    "/images/p10.jpeg",
    "/images/p12.jpeg",
    "/images/p13.jpeg",
    "/images/p14.jpeg",
    "/images/p15.jpeg",
    "/images/p16.jpeg",
    "/images/p17.jpeg",
    "/images/p18.jpeg",
    "/images/p19.jpeg",
    "/images/p21.jpeg",
    "/images/p22.jpeg",
    "/images/p23.jpeg",
    "/images/p24.jpeg",
    "/images/p25.jpeg",
    "/images/p26.jpeg",
    "/images/p27.jpeg",
    "/images/p28.jpeg",
    "/images/p30.jpeg",
    "/images/p31.jpeg",
    "/images/p32.jpeg",
    "/images/p33.jpeg",
    "/images/p34.jpeg",
    "/images/p35.jpeg",
    "/images/p36.jpeg",
    "/images/p37.jpeg",
    "/images/p38.jpeg",
    "/images/p39.jpeg",
    "/images/p40.jpeg",
    "/images/p41.jpeg",
    "/images/p42.jpeg",
  ];

  const [activeIndex, setActiveIndex] = useState(null);
  const isOpen = activeIndex !== null;

  const close = useCallback(() => setActiveIndex(null), []);
  const showPrev = useCallback(
    () => setActiveIndex((i) => (i === 0 ? images.length - 1 : i - 1)),
    [images.length]
  );
  const showNext = useCallback(
    () => setActiveIndex((i) => (i === images.length - 1 ? 0 : i + 1)),
    [images.length]
  );

  // Keyboard navigation + preload images + lock scroll
useEffect(() => {
  if (!isOpen) return;

  const onKeyDown = (e) => {
    if (e.key === "Escape") close();
    if (e.key === "ArrowLeft") showPrev();
    if (e.key === "ArrowRight") showNext();
  };

  document.addEventListener("keydown", onKeyDown);

  const previousOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";

  // Preload current, previous and next images
  const indexesToPreload = [
    activeIndex,
    activeIndex === 0 ? images.length - 1 : activeIndex - 1,
    activeIndex === images.length - 1 ? 0 : activeIndex + 1,
  ];

  const preloadedImages = indexesToPreload.map((index) => {
    const img = new Image();
    img.src = images[index];
    return img;
  });

  return () => {
    document.removeEventListener("keydown", onKeyDown);
    document.body.style.overflow = previousOverflow;

    // Release references
    preloadedImages.forEach((img) => {
      img.onload = null;
      img.onerror = null;
    });
  };
}, [isOpen, activeIndex, close, showPrev, showNext, images]);

  return (
    <div className="min-h-screen bg-background">
      {/* HERO */}
      <section className="relative py-24 md:py-32 bg-foreground text-background overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/70" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <p className="text-electric-blue uppercase tracking-[0.25em] text-sm font-semibold mb-4">
              {t("gallery.hero.eyebrow")}
          </p>

          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold mb-6">
             {t("gallery.hero.title")}
          </h1>

          <p className="font-paragraph text-base md:text-lg text-background/70 max-w-2xl mx-auto leading-relaxed">
             {t("gallery.hero.description")}
          </p>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-[120rem] mx-auto px-5 sm:px-6 md:px-12">
          {/* TITLE */}
          <div className="text-center mb-12 md:mb-16">
            <p className="text-primary font-semibold uppercase tracking-[0.2em] text-sm mb-3">
               {t("gallery.section.eyebrow")}
            </p>

            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-foreground">
              {t("gallery.section.title")}
            </h2>

            <p className="mt-4 text-foreground/70 max-w-2xl mx-auto">
               {t("gallery.section.description")}
            </p>
          </div>

          {/* MASONRY */}
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5 space-y-5">
            {images.map((image, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActiveIndex(index)}
                className="relative block w-full break-inside-avoid overflow-hidden rounded-2xl group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <img
                  src={image}
                  alt={`Moment de l'Association des Leaders d'Excellence ${index + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="w-10 h-10 rounded-full bg-background/90 flex items-center justify-center scale-90 group-hover:scale-100 transition-transform duration-300">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="w-5 h-5 text-foreground"
                    >
                      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                    </svg>
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={close}
        >
          {/* Close */}
          <button
            type="button"
            onClick={close}
            aria-label="Fermer"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Prev */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              showPrev();
            }}
            aria-label="Image précédente"
            className="absolute left-3 md:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Next */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              showNext();
            }}
            aria-label="Image suivante"
            className="absolute right-3 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Image */}
          <img
            src={images[activeIndex]}
            alt={`Moment de l'Association des Leaders d'Excellence ${activeIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
          />

          {/* Counter */}
          <div
            className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium tracking-wide"
            onClick={(e) => e.stopPropagation()}
          >
            {activeIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
}